import { Box, HStack, Text } from '@chakra-ui/react';
import { FC } from 'react';
import { MdChevronRight } from 'react-icons/md';

type DisclosureVariant = 'default' | 'success' | 'warning' | 'error';

const variantStyles: Record<
  DisclosureVariant,
  {
    bg: string;
    borderColor?: string;
    textColor: string;
    hoverBg: string;
  }
> = {
  default: {
    bg: 'bg.default',
    borderColor: 'border.default',
    textColor: 'text.default',
    hoverBg: 'bg.subtle',
  },
  success: {
    bg: 'bg.brand',
    textColor: 'text.brand',
    hoverBg: 'green.200',
  },
  warning: {
    bg: 'orange.50',
    textColor: 'status.warning',
    hoverBg: 'orange.100',
  },
  error: {
    bg: 'red.50',
    textColor: 'status.error',
    hoverBg: 'red.100',
  },
};

export interface DisclosureProps {
  text: string;
  variant?: DisclosureVariant;
  onClick?: () => void;
  'data-testid'?: string;
}

/**
 * A notification-style disclosure row with optional chevron for clickable items.
 */
export const Disclosure: FC<DisclosureProps> = ({
  text,
  variant = 'default',
  onClick,
  'data-testid': testId = 'disclosure',
}) => {
  const { bg, borderColor, textColor, hoverBg } = variantStyles[variant];

  return (
    <Box
      bg={bg}
      borderRadius="lg"
      border={borderColor ? '1px solid' : undefined}
      borderColor={borderColor}
      px={4}
      py={3}
      cursor={onClick ? 'pointer' : 'default'}
      onClick={onClick}
      _hover={onClick ? { bg: hoverBg } : undefined}
      data-testid={testId}
    >
      <HStack justify="space-between">
        <Text fontSize="sm" color={textColor}>
          {text}
        </Text>
        {onClick && (
          <Box as={MdChevronRight} color={textColor} flexShrink={0} />
        )}
      </HStack>
    </Box>
  );
};

Disclosure.displayName = 'Disclosure';
